import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { doGet } from '../services/http';
import { errorHandler, getAsyncThunkMsg } from '../services/Utils';

/**
 * Async thunk to fetch canteen details (categories, items, slots)
 * @param {Object} params - Query parameters including canteen_id
 * @returns {Promise<Object>} Canteen data
 */
export const fetchCanteen = createAsyncThunk(
  'canteen/fetchCanteen',
  async (params, { rejectWithValue }) => {
    try {
      const response = await doGet('canteen', params);
      return response.data;
    } catch (err) {
      console.error('fetchCanteen error:', err);
      // Centralized logging
      errorHandler(err);
      // Normalized error message
      return rejectWithValue(getAsyncThunkMsg(err));
    }
  }
);

/**
 * Redux slice for canteen/restaurant menu state
 * Manages categories, items, slots, and item type filters
 */
const CanteenSlice = createSlice({
  name: 'canteen',
  initialState: {
    canteen_data: null,
    canteen: null,
    categories: [],
    items: [],
    slots: [],
    status: 'idle',
    error: null,
    refreshing: false,
    item_type: null,
  },
  reducers: {
    /** Enables pull-to-refresh control */
    enableRefreshControl(state, action) {
      if (!action.payload?.retry) {
        state.refreshing = true;
      }
    },
    /** Disables pull-to-refresh control */
    disableRefreshControl(state, action) {
      state.refreshing = false;
    },
    /** Resets canteen data (categories, items, slots) */
    resetCanteen(state, action) {
      state.categories = [];
      state.items = [];
      state.slots = [];
      state.item_type = null;
    },
    /** Sets basic canteen info */
    setCanteenInfo(state, action) {
      state.canteen = action.payload;
    },
    /** Sets complete canteen data (categories, items, slots) */
    setCanteen(state, action) {
      state.categories = action.payload?.categories;
      state.items = action.payload?.items;
      state.slots = action.payload?.slots || [];
    },
    /** Sets item type filter (veg/non-veg/all) */
    setItemType(state, action) {
      state.item_type = action.payload;
    },
    /** Sets available time slots for pre-orders */
    setSlots(state, action) {
      state.slots = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCanteen.pending, (state, action) => {
      if (!state.refreshing) {
        state.status = 'loading';
      }
    })
      .addCase(fetchCanteen.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.canteen_data = action.payload;
        if (!state.canteen) {
          state.canteen = action.payload?.canteen;
        }
        state.categories = action.payload?.categories;
        state.items = action.payload?.items;
        state.slots = action.payload?.slots || [];
        state.refreshing = false;
      })
      .addCase(fetchCanteen.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action?.payload;
        state.refreshing = false;
      });
  },
});

export const { setCanteen, enableRefreshControl, resetCanteen, setItemType, setCanteenInfo, setSlots } =
  CanteenSlice.actions;
export default CanteenSlice.reducer;
